/**
 * Unauthorized Page
 *
 * Displayed when a user tries to access a route or resource they don't have permission for.
 * Shows:
 * - Clear access denied message
 * - Required permission/feature details
 * - User's current role and permissions (optional)
 * - Helpful actions (go back, contact admin, view permissions)
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { ShieldX, ArrowLeft, Mail, Eye, Lock, AlertTriangle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../contexts/PermissionContext';
import { useFeatures } from '../contexts/FeatureContext';

export const Unauthorized: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, isAuthenticated } = useAuth();
  const { permissions } = usePermissions();
  const { features } = useFeatures();

  const [showDetails, setShowDetails] = useState(false);

  // Get required permission/feature from URL params
  const requiredPermission = searchParams.get('permission');
  const requiredFeature = searchParams.get('feature');
  const requiredRole = searchParams.get('role');
  const attemptedPath = searchParams.get('path') || 'this resource';

  // Determine denial reason
  const getDenialReason = () => {
    if (!isAuthenticated) {
      return {
        title: 'Authentication Required',
        message: 'You must be logged in to access this resource.',
        icon: <Lock className="w-16 h-16 text-orange-500" />,
        color: 'orange',
      };
    }

    if (requiredRole) {
      return {
        title: 'Insufficient Role',
        message: `This page requires ${requiredRole} role access.`,
        icon: <ShieldX className="w-16 h-16 text-red-500" />,
        color: 'red',
      };
    }

    if (requiredPermission) {
      return {
        title: 'Access Denied',
        message: 'You do not have the required permission to access this page.',
        icon: <ShieldX className="w-16 h-16 text-red-500" />,
        color: 'red',
      };
    }

    if (requiredFeature) {
      return {
        title: 'Feature Not Available',
        message: 'This feature is not included in your subscription plan.',
        icon: <AlertTriangle className="w-16 h-16 text-yellow-500" />,
        color: 'yellow',
      };
    }

    return {
      title: 'Access Denied',
      message: 'You do not have permission to access this resource.',
      icon: <ShieldX className="w-16 h-16 text-red-500" />,
      color: 'red',
    };
  };

  const denial = getDenialReason();

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-12">
      <div className="max-w-2xl w-full">
        {/* Main Card */}
        <div className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
          {/* Header */}
          <div className={`bg-gradient-to-r from-${denial.color}-50 to-${denial.color}-100 border-b border-${denial.color}-200 p-8`}>
            <div className="flex flex-col items-center text-center">
              {denial.icon}
              <h1 className={`text-3xl font-bold text-${denial.color}-900 mt-4`}>
                {denial.title}
              </h1>
              <p className="text-gray-700 mt-2 text-lg">
                {denial.message}
              </p>
            </div>
          </div>

          {/* Content */}
          <div className="p-8 space-y-6">
            {/* Required Permission/Feature Details */}
            {(requiredPermission || requiredFeature || requiredRole) && (
              <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900 mb-3">
                  Required Access
                </h2>

                {requiredRole && (
                  <div className="flex items-start gap-3 mb-3">
                    <ShieldX className="w-5 h-5 text-red-500 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-gray-700">Role Required</p>
                      <p className="text-sm text-gray-600 font-mono bg-white px-2 py-1 rounded border border-gray-200 inline-block mt-1">
                        {requiredRole}
                      </p>
                    </div>
                  </div>
                )}

                {requiredPermission && (
                  <div className="flex items-start gap-3 mb-3">
                    <ShieldX className="w-5 h-5 text-red-500 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-gray-700">Permission Required</p>
                      <p className="text-sm text-gray-600 font-mono bg-white px-2 py-1 rounded border border-gray-200 inline-block mt-1">
                        {requiredPermission}
                      </p>
                    </div>
                  </div>
                )}

                {requiredFeature && (
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-yellow-500 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-gray-700">Feature Required</p>
                      <p className="text-sm text-gray-600 font-mono bg-white px-2 py-1 rounded border border-gray-200 inline-block mt-1">
                        {requiredFeature}
                      </p>
                    </div>
                  </div>
                )}

                <div className="mt-4 text-sm text-gray-600">
                  <p>Attempted to access: <span className="font-medium">{attemptedPath}</span></p>
                </div>
              </div>
            )}

            {/* Current User Info */}
            {isAuthenticated && user && (
              <div className="bg-blue-50 rounded-lg p-6 border border-blue-200">
                <h2 className="text-lg font-semibold text-gray-900 mb-3">
                  Your Account
                </h2>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Email:</span>
                    <span className="font-medium text-gray-900">{user.email}</span>
                  </div>
                  {user.role && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Role:</span>
                      <span className="font-medium text-gray-900 capitalize">
                        {user.role.replace('_', ' ')}
                      </span>
                    </div>
                  )}
                  {user.companyName && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Company:</span>
                      <span className="font-medium text-gray-900">{user.companyName}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-gray-600">Permissions:</span>
                    <span className="font-medium text-gray-900">{permissions.length} active</span>
                  </div>
                </div>
              </div>
            )}

            {/* Show Details Toggle */}
            {isAuthenticated && permissions.length > 0 && (
              <div>
                <button
                  onClick={() => setShowDetails(!showDetails)}
                  className="flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium text-sm"
                >
                  <Eye className="w-4 h-4" />
                  {showDetails ? 'Hide' : 'View'} Your Permissions
                </button>

                {showDetails && (
                  <div className="mt-4 bg-gray-50 rounded-lg p-4 border border-gray-200 max-h-64 overflow-y-auto">
                    <h3 className="text-sm font-semibold text-gray-900 mb-3">
                      Your Active Permissions ({permissions.length})
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {permissions.sort().map((perm) => (
                        <div
                          key={perm}
                          className="text-xs text-gray-600 font-mono bg-white px-2 py-1 rounded border border-gray-200"
                        >
                          {perm}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* What You Can Do */}
            <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900 mb-3">
                What You Can Do
              </h2>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-start gap-2">
                  <span className="text-blue-600 mt-0.5">•</span>
                  <span>Go back to the previous page and try a different action</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-600 mt-0.5">•</span>
                  <span>Contact your administrator to request access</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-600 mt-0.5">•</span>
                  <span>Return to the dashboard and explore available features</span>
                </li>
                {requiredFeature && (
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 mt-0.5">•</span>
                    <span>Upgrade your subscription to access this feature</span>
                  </li>
                )}
              </ul>
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={() => navigate(-1)}
                className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Go Back
              </button>

              <Link
                to="/dashboard"
                className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Go to Dashboard
              </Link>

              {isAuthenticated && (
                <a
                  href="mailto:admin@tsap.ai?subject=Access Request&body=I need access to: [describe what you need]"
                  className="flex-1 flex items-center justify-center gap-2 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <Mail className="w-4 h-4" />
                  Contact Admin
                </a>
              )}
            </div>

            {/* Additional Help for Features */}
            {requiredFeature && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5" />
                  <div className="flex-1">
                    <h3 className="text-sm font-semibold text-yellow-900 mb-1">
                      Feature Not Available
                    </h3>
                    <p className="text-sm text-yellow-800 mb-3">
                      The <span className="font-mono font-semibold">{requiredFeature}</span> feature
                      is not included in your current subscription plan.
                    </p>
                    <button
                      onClick={() => navigate('/pricing')}
                      className="text-sm font-medium text-yellow-900 hover:text-yellow-700 underline"
                    >
                      View Subscription Plans →
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer Help Text */}
        <div className="mt-6 text-center text-sm text-gray-500">
          <p>
            If you believe this is an error, please contact your administrator or{' '}
            <a href="mailto:support@tsap.ai" className="text-blue-600 hover:text-blue-700 underline">
              support@tsap.ai
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Unauthorized;
